import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, timer } from 'rxjs';
import { retry } from 'rxjs/operators';

/**
 * Interceptor for HTTP retry with exponential backoff
 *
 * Retries GET requests on transient network errors (status 0, 502, 503, 504)
 * with exponential backoff delays of 1s, 2s, 4s (max 3 retries).
 *
 * Does NOT retry:
 * - Non-GET requests (POST, PUT, PATCH, DELETE are not idempotent)
 * - Client errors (400, 401, 403, 404, 422)
 * - Aborted requests (AbortError)
 */
@Injectable()
export class RetryInterceptor implements HttpInterceptor {
	private static readonly MAX_RETRIES = 3;
	private static readonly RETRYABLE_STATUS_CODES = new Set([0, 502, 503, 504]);

	intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		if (request.method !== 'GET') {
			return next.handle(request);
		}

		return next.handle(request).pipe(
			retry({
				count: RetryInterceptor.MAX_RETRIES,
				delay: (error: unknown, retryCount: number) => {
					if (!RetryInterceptor.isRetryable(error)) {
						throw error;
					}
					const delayMs = Math.pow(2, retryCount - 1) * 1000;
					return timer(delayMs);
				}
			})
		);
	}

	private static isRetryable(error: unknown): boolean {
		if (!(error instanceof HttpErrorResponse)) {
			return false;
		}
		if (error.error instanceof DOMException && error.error.name === 'AbortError') {
			return false;
		}
		return RetryInterceptor.RETRYABLE_STATUS_CODES.has(error.status);
	}
}
